// src/cockpit4/components/WorkflowMonitor.tsx
// ============================================
// 执行监控与告警 - 工作流中心 P0-功能3
//
// 功能（改进计划文档 9.2 功能3）：
//   1. 实时执行状态监控（运行中/成功/失败/排队/超时/取消）
//   2. 失败自动告警 + 可配置自动重试
//   3. 执行日志和节点级追溯（输入/输出/错误）
//   4. 性能指标统计（成功率/平均耗时/失败率）
//   5. 执行趋势图（recharts AreaChart，按日成功/失败数）
//   6. 慢节点识别（平均耗时 Top5）
//   7. 手动重试失败节点
//   8. 告警规则配置（失败/超时/慢节点/高错误率 + 自动重试配置）
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   - 执行记录：localStorage(key: xeaos_workflow_executions) 持久化
//   - 实时状态：RuntimeManager.query(RuntimeName.Workflow, { type: 'execution_status' })
//     （RuntimeName.Workflow === 'workflow'，运行中执行每 5 秒轮询同步）
//   - 告警规则：localStorage(key: xeaos_workflow_alert_rules) 持久化
//   - 重试操作：RuntimeManager.execute(RuntimeName.Workflow, 'retry', { executionId, nodeId })
//   无数据时显示"暂无执行记录"，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  AlertTriangle,
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Cpu,
  Eye,
  ListTree,
  Loader2,
  RefreshCw,
  Settings,
  Snail,
  TrendingDown,
  TrendingUp,
  X,
  XCircle,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

// ============================================
// 类型定义（与改进计划文档数据模型对齐）
// ============================================

export type ExecutionStatus =
  | "running"
  | "success"
  | "failed"
  | "queued"
  | "timeout"
  | "cancelled";

export interface NodeExecution {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  status: ExecutionStatus;
  startTime: string;
  endTime?: string;
  duration?: number; // 耗时（毫秒）
  input?: string;
  output?: string;
  error?: string;
  retryCount: number;
}

export interface WorkflowExecution {
  id: string;
  workflowName: string;
  status: ExecutionStatus;
  startTime: string;
  endTime?: string;
  duration?: number;
  nodeExecutions: NodeExecution[];
  triggeredBy: string; // 触发方式（manual/schedule/event）
  error?: string;
}

export interface AlertRule {
  id: string;
  name: string;
  type: "failure" | "timeout" | "slow_node" | "high_error_rate";
  enabled: boolean;
  threshold?: number;
  retryConfig?: {
    maxRetries: number;
    interval: number; // 重试间隔（秒）
  };
}

export interface PerformanceMetrics {
  totalExecutions: number;
  successCount: number;
  failedCount: number;
  successRate: number;
  avgDuration: number;
  failureRate: number;
  slowestNodes: { nodeId: string; nodeName: string; avgDuration: number }[];
}

// 主动告警条目（运行时生成，不持久化）
interface ActiveAlert {
  id: string;
  ruleType: AlertRule["type"];
  severity: "info" | "warning" | "critical";
  message: string;
  executionId?: string;
  nodeId?: string;
  createdAt: string;
}

// ============================================
// 常量
// ============================================

const STORAGE_KEY = "xeaos_workflow_executions";
const ALERT_RULES_KEY = "xeaos_workflow_alert_rules";

// 状态展示配置（颜色 / 背景 / 边框）
const STATUS_CONFIG: Record<
  ExecutionStatus,
  { label: string; color: string; bg: string; border: string }
> = {
  running: { label: "运行中", color: "#60A5FA", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  success: { label: "成功", color: "#34D399", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  failed: { label: "失败", color: "#F87171", bg: "bg-red-500/10", border: "border-red-500/30" },
  queued: { label: "排队", color: "#FBBF24", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  timeout: { label: "超时", color: "#FB923C", bg: "bg-orange-500/10", border: "border-orange-500/30" },
  cancelled: { label: "已取消", color: "#9CA3AF", bg: "bg-gray-500/10", border: "border-gray-500/30" },
};

// 默认告警规则（首次加载时写入 localStorage）
const DEFAULT_ALERT_RULES: AlertRule[] = [
  {
    id: "rule_failure",
    name: "失败告警",
    type: "failure",
    enabled: true,
    retryConfig: { maxRetries: 3, interval: 5 },
  },
  {
    id: "rule_timeout",
    name: "超时告警",
    type: "timeout",
    enabled: true,
    threshold: 30000, // 30 秒
  },
  {
    id: "rule_slow_node",
    name: "慢节点告警",
    type: "slow_node",
    enabled: true,
    threshold: 10000, // 10 秒
  },
  {
    id: "rule_high_error_rate",
    name: "高错误率告警",
    type: "high_error_rate",
    enabled: true,
    threshold: 30, // 30%
  },
];

// 触发方式展示配置
const TRIGGER_CONFIG: Record<string, { label: string; color: string }> = {
  manual: { label: "手动", color: "#60A5FA" },
  schedule: { label: "定时", color: "#FBBF24" },
  event: { label: "事件", color: "#A78BFA" },
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix = "id"): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 毫秒转可读时长 */
function formatDuration(ms?: number): string {
  if (ms === undefined || ms === null || isNaN(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.round((ms % 60000) / 1000);
  return `${m}m${s}s`;
}

/** ISO 时间转 HH:MM:SS */
function formatTime(iso?: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString("zh-CN", { hour12: false });
  } catch {
    return "—";
  }
}

/** ISO 时间转 MM-DD HH:MM */
function formatDateTime(iso?: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${mm}-${dd} ${hh}:${mi}`;
  } catch {
    return "—";
  }
}

/** 读取 localStorage 执行记录 */
function loadExecutions(): WorkflowExecution[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as WorkflowExecution[]) : [];
  } catch {
    return [];
  }
}

/** 写入 localStorage 执行记录 */
function saveExecutions(execs: WorkflowExecution[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(execs));
  } catch {
    // 容量超限或隐私模式，静默失败
  }
}

/** 读取告警规则（无则写入默认） */
function loadAlertRules(): AlertRule[] {
  try {
    const raw = localStorage.getItem(ALERT_RULES_KEY);
    if (!raw) {
      localStorage.setItem(ALERT_RULES_KEY, JSON.stringify(DEFAULT_ALERT_RULES));
      return DEFAULT_ALERT_RULES;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AlertRule[]) : DEFAULT_ALERT_RULES;
  } catch {
    return DEFAULT_ALERT_RULES;
  }
}

/** 写入告警规则 */
function saveAlertRules(rules: AlertRule[]): void {
  try {
    localStorage.setItem(ALERT_RULES_KEY, JSON.stringify(rules));
  } catch {
    // 静默失败
  }
}

/** 获取触发方式展示配置 */
function getTriggerConfig(trigger: string) {
  return TRIGGER_CONFIG[trigger] || { label: trigger || "未知", color: "#9CA3AF" };
}

// ============================================
// 子组件：状态标签
// ============================================
function StatusBadge({ status }: { status: ExecutionStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${cfg.bg} ${cfg.border} border`}
      style={{ color: cfg.color }}
    >
      {status === "running" && <Loader2 size={9} className="animate-spin" />}
      <span>{cfg.label}</span>
    </span>
  );
}

// ============================================
// 子组件：KPI 卡片（顶部大盘）
// ============================================
interface MiniKpiProps {
  label: string;
  value: string | number;
  icon: typeof Activity;
  color: string;
  delay?: number;
}

function MiniKpi({ label, value, icon: Icon, color, delay = 0 }: MiniKpiProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
      className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3 overflow-hidden"
    >
      {/* 顶部光带 */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${color}80, transparent)` }}
      />
      {/* 角落光晕 */}
      <div
        className="absolute -top-8 -right-8 w-16 h-16 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`, filter: "blur(16px)" }}
      />
      <div className="flex items-center gap-2 mb-1.5 relative">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center"
          style={{ background: `${color}15`, border: `1px solid ${color}30` }}
        >
          <Icon size={12} style={{ color }} />
        </div>
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="text-[20px] font-mono font-bold text-white tracking-tight leading-none relative">
        {value}
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：空状态
// ============================================
function EmptyState({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="py-8 text-center">
      <div className="text-[12px] font-mono text-gray-600 italic">{text}</div>
      {sub && <div className="text-[10px] font-mono text-gray-700 mt-1.5">{sub}</div>}
    </div>
  );
}

// ============================================
// 主组件
// ============================================
export default function WorkflowMonitor() {
  // ===== 状态 =====
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [alertRules, setAlertRules] = useState<AlertRule[]>(DEFAULT_ALERT_RULES);
  const [activeAlerts, setActiveAlerts] = useState<ActiveAlert[]>([]);
  const [realtimeData, setRealtimeData] = useState<any>(null);
  const [runtimeAvailable, setRuntimeAvailable] = useState<boolean>(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAlertConfig, setShowAlertConfig] = useState<boolean>(false);
  const [logExecution, setLogExecution] = useState<WorkflowExecution | null>(null);
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<boolean>(true);

  // 已自动重试的执行 ID 集合（避免重复触发）
  const autoRetriedRef = useRef<Set<string>>(new Set());
  // 已告警的执行/节点键集合（避免重复告警）
  const alertedRef = useRef<Set<string>>(new Set());

  // ===== 初始化加载（localStorage） =====
  useEffect(() => {
    setExecutions(loadExecutions());
    setAlertRules(loadAlertRules());
    setLoading(false);
  }, []);

  // ===== 实时状态轮询：RuntimeManager.query('workflow', { type: 'execution_status' }) =====
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        if (!RuntimeManager.hasRuntime(RuntimeName.Workflow)) {
          if (!cancelled) setRuntimeAvailable(false);
          return;
        }
        if (!cancelled) setRuntimeAvailable(true);
        const resp = await RuntimeManager.query(RuntimeName.Workflow, {
          type: "execution_status",
        });
        if (!cancelled && resp.success) {
          setRealtimeData(resp.data);
        }
      } catch {
        // 静默失败，不影响 localStorage 数据展示
      }
    };
    poll();
    const timer = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  // ===== 同步实时运行中状态到 localStorage 记录 =====
  // 实时数据中的运行中执行若已存在于 localStorage，则更新其状态为 running
  useEffect(() => {
    if (!realtimeData) return;
    // 兼容多种返回结构：数组 / { executions: [] } / { data: [] }
    let runningList: any[] = [];
    if (Array.isArray(realtimeData)) {
      runningList = realtimeData;
    } else if (Array.isArray(realtimeData?.executions)) {
      runningList = realtimeData.executions;
    } else if (Array.isArray(realtimeData?.data)) {
      runningList = realtimeData.data;
    }
    if (runningList.length === 0) return;
    setExecutions((prev) => {
      let changed = false;
      const next = prev.map((e) => {
        const rt = runningList.find(
          (r: any) => r?.id === e.id || r?.executionId === e.id
        );
        if (rt && e.status !== "running") {
          changed = true;
          return { ...e, status: "running" as ExecutionStatus };
        }
        return e;
      });
      if (changed) saveExecutions(next);
      return changed ? next : prev;
    });
  }, [realtimeData]);

  // ===== 手动 / 自动重试失败节点 =====
  const retryExecution = useCallback(
    async (execId: string, nodeId?: string) => {
      const retryKey = nodeId ? `${execId}_${nodeId}` : execId;
      setRetrying((prev) => ({ ...prev, [retryKey]: true }));
      try {
        // 调用真实 Runtime 重试（若已注册）
        if (RuntimeManager.hasRuntime(RuntimeName.Workflow)) {
          await RuntimeManager.execute(RuntimeName.Workflow, "retry", {
            executionId: execId,
            nodeId,
          });
        }
        // 更新 localStorage 记录：递增 retryCount，重置失败节点为 running
        setExecutions((prev) => {
          const next = prev.map((e) => {
            if (e.id !== execId) return e;
            const updatedNodes = e.nodeExecutions.map((n) => {
              if (nodeId && n.nodeId !== nodeId) return n;
              if (!nodeId && n.status !== "failed" && n.status !== "timeout") return n;
              return {
                ...n,
                retryCount: n.retryCount + 1,
                status: "running" as ExecutionStatus,
                error: undefined,
              };
            });
            return {
              ...e,
              status: "running" as ExecutionStatus,
              error: undefined,
              nodeExecutions: updatedNodes,
            };
          });
          saveExecutions(next);
          return next;
        });
      } catch (err) {
        // 重试失败，记录告警（不伪造成功结果）
        setActiveAlerts((prev) =>
          [
            {
              id: genId("alert"),
              ruleType: "failure" as const,
              severity: "warning" as const,
              message: `重试失败：${err instanceof Error ? err.message : String(err)}`,
              executionId: execId,
              nodeId,
              createdAt: new Date().toISOString(),
            },
            ...prev,
          ].slice(0, 20)
        );
      } finally {
        setRetrying((prev) => ({ ...prev, [retryKey]: false }));
      }
    },
    []
  );

  // ===== 告警检测 + 自动重试 =====
  useEffect(() => {
    if (executions.length === 0) return;
    const newAlerts: ActiveAlert[] = [];
    const now = Date.now();

    const failureRule = alertRules.find((r) => r.type === "failure" && r.enabled);
    const timeoutRule = alertRules.find((r) => r.type === "timeout" && r.enabled);
    const slowRule = alertRules.find((r) => r.type === "slow_node" && r.enabled);
    const highErrorRule = alertRules.find((r) => r.type === "high_error_rate" && r.enabled);

    for (const exec of executions) {
      // 失败告警
      if (
        failureRule &&
        (exec.status === "failed" || exec.status === "timeout") &&
        !alertedRef.current.has(`fail_${exec.id}`)
      ) {
        alertedRef.current.add(`fail_${exec.id}`);
        newAlerts.push({
          id: genId("alert"),
          ruleType: "failure",
          severity: "critical",
          message: `工作流「${exec.workflowName}」执行${STATUS_CONFIG[exec.status].label}：${exec.error || "未知错误"}`,
          executionId: exec.id,
          createdAt: new Date().toISOString(),
        });
      }
      // 超时告警（运行中且超过阈值）
      if (
        timeoutRule &&
        timeoutRule.threshold &&
        exec.status === "running" &&
        exec.startTime
      ) {
        const elapsed = now - new Date(exec.startTime).getTime();
        const key = `timeout_${exec.id}`;
        if (elapsed > timeoutRule.threshold && !alertedRef.current.has(key)) {
          alertedRef.current.add(key);
          newAlerts.push({
            id: genId("alert"),
            ruleType: "timeout",
            severity: "warning",
            message: `工作流「${exec.workflowName}」已运行 ${formatDuration(elapsed)}，超过阈值 ${formatDuration(timeoutRule.threshold)}`,
            executionId: exec.id,
            createdAt: new Date().toISOString(),
          });
        }
      }
      // 慢节点告警
      if (slowRule && slowRule.threshold) {
        for (const node of exec.nodeExecutions) {
          const key = `slow_${exec.id}_${node.nodeId}`;
          if (
            typeof node.duration === "number" &&
            node.duration > slowRule.threshold &&
            !alertedRef.current.has(key)
          ) {
            alertedRef.current.add(key);
            newAlerts.push({
              id: genId("alert"),
              ruleType: "slow_node",
              severity: "warning",
              message: `节点「${node.nodeName}」耗时 ${formatDuration(node.duration)}，超过阈值 ${formatDuration(slowRule.threshold)}`,
              executionId: exec.id,
              nodeId: node.nodeId,
              createdAt: new Date().toISOString(),
            });
          }
        }
      }
    }

    // 高错误率告警（基于最近 20 次执行，每分钟最多一次）
    if (highErrorRule && highErrorRule.threshold && executions.length >= 5) {
      const recent = executions.slice(0, 20);
      const recentFailed = recent.filter(
        (e) => e.status === "failed" || e.status === "timeout"
      ).length;
      const recentRate = (recentFailed / recent.length) * 100;
      const key = `high_error_${Math.floor(now / 60000)}`;
      if (recentRate > highErrorRule.threshold && !alertedRef.current.has(key)) {
        alertedRef.current.add(key);
        newAlerts.push({
          id: genId("alert"),
          ruleType: "high_error_rate",
          severity: "critical",
          message: `最近 ${recent.length} 次执行错误率达 ${recentRate.toFixed(1)}%，超过阈值 ${highErrorRule.threshold}%`,
          createdAt: new Date().toISOString(),
        });
      }
    }

    if (newAlerts.length > 0) {
      setActiveAlerts((prev) => [...newAlerts, ...prev].slice(0, 20));
    }

    // ===== 自动重试（失败告警 + retryConfig） =====
    if (failureRule?.retryConfig) {
      for (const exec of executions) {
        if (
          (exec.status === "failed" || exec.status === "timeout") &&
          !autoRetriedRef.current.has(exec.id) &&
          exec.nodeExecutions.some(
            (n) =>
              (n.status === "failed" || n.status === "timeout") &&
              n.retryCount < failureRule.retryConfig!.maxRetries
          )
        ) {
          autoRetriedRef.current.add(exec.id);
          // 按配置间隔延迟自动重试
          setTimeout(() => {
            void retryExecution(exec.id);
            setActiveAlerts((prev) =>
              [
                {
                  id: genId("alert"),
                  ruleType: "failure" as const,
                  severity: "info" as const,
                  message: `工作流「${exec.workflowName}」已自动重试（最大重试 ${failureRule.retryConfig!.maxRetries} 次）`,
                  executionId: exec.id,
                  createdAt: new Date().toISOString(),
                },
                ...prev,
              ].slice(0, 20)
            );
          }, failureRule.retryConfig.interval * 1000);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executions, alertRules]);

  // ===== 性能指标计算 =====
  const metrics: PerformanceMetrics = useMemo(() => {
    const completed = executions.filter(
      (e) =>
        e.status === "success" ||
        e.status === "failed" ||
        e.status === "timeout" ||
        e.status === "cancelled"
    );
    const successCount = executions.filter((e) => e.status === "success").length;
    const failedCount = executions.filter(
      (e) => e.status === "failed" || e.status === "timeout"
    ).length;
    const total = completed.length;
    const successRate = total > 0 ? (successCount / total) * 100 : 0;
    const failureRate = total > 0 ? (failedCount / total) * 100 : 0;
    const durations = completed
      .map((e) => e.duration)
      .filter((d): d is number => typeof d === "number" && d > 0);
    const avgDuration =
      durations.length > 0 ? durations.reduce((s, d) => s + d, 0) / durations.length : 0;

    // 慢节点统计：按 nodeId 聚合平均耗时
    const nodeMap = new Map<
      string,
      { nodeId: string; nodeName: string; total: number; count: number }
    >();
    for (const exec of executions) {
      for (const node of exec.nodeExecutions) {
        if (
          typeof node.duration === "number" &&
          node.duration > 0 &&
          (node.status === "success" ||
            node.status === "failed" ||
            node.status === "timeout")
        ) {
          const existing = nodeMap.get(node.nodeId);
          if (existing) {
            existing.total += node.duration;
            existing.count += 1;
          } else {
            nodeMap.set(node.nodeId, {
              nodeId: node.nodeId,
              nodeName: node.nodeName,
              total: node.duration,
              count: 1,
            });
          }
        }
      }
    }
    const slowestNodes = Array.from(nodeMap.values())
      .map((n) => ({
        nodeId: n.nodeId,
        nodeName: n.nodeName,
        avgDuration: n.total / n.count,
      }))
      .sort((a, b) => b.avgDuration - a.avgDuration)
      .slice(0, 5);

    return {
      totalExecutions: executions.length,
      successCount,
      failedCount,
      successRate,
      avgDuration,
      failureRate,
      slowestNodes,
    };
  }, [executions]);

  // ===== 顶部 KPI =====
  const kpis = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const todayExecs = executions.filter(
      (e) => (e.startTime || "").slice(0, 10) === todayStr
    );
    const running = executions.filter((e) => e.status === "running").length;
    const todaySuccess = todayExecs.filter((e) => e.status === "success").length;
    const todayFailed = todayExecs.filter(
      (e) => e.status === "failed" || e.status === "timeout"
    ).length;
    return {
      running,
      todaySuccess,
      todayFailed,
      avgDuration: metrics.avgDuration,
      successRate: metrics.successRate,
    };
  }, [executions, metrics]);

  // ===== 趋势数据（最近 14 天，按日成功/失败数） =====
  const trendData = useMemo(() => {
    const days = 14;
    const result: { date: string; label: string; success: number; failed: number }[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().slice(0, 10);
      const dayExecs = executions.filter(
        (e) => (e.startTime || "").slice(0, 10) === dateStr
      );
      result.push({
        date: dateStr,
        label: `${d.getMonth() + 1}/${d.getDate()}`,
        success: dayExecs.filter((e) => e.status === "success").length,
        failed: dayExecs.filter(
          (e) => e.status === "failed" || e.status === "timeout"
        ).length,
      });
    }
    return result;
  }, [executions]);

  // ===== 按开始时间倒序 =====
  const sortedExecutions = useMemo(
    () =>
      [...executions].sort(
        (a, b) =>
          new Date(b.startTime || 0).getTime() - new Date(a.startTime || 0).getTime()
      ),
    [executions]
  );

  // ===== 保存告警规则 =====
  const handleSaveAlertRules = useCallback((rules: AlertRule[]) => {
    setAlertRules(rules);
    saveAlertRules(rules);
    setShowAlertConfig(false);
  }, []);

  // ===== 关闭告警 =====
  const dismissAlert = useCallback((alertId: string) => {
    setActiveAlerts((prev) => prev.filter((a) => a.id !== alertId));
  }, []);

  const hasData = executions.length > 0;

  // ===== 渲染 =====
  return (
    <div className="space-y-3">
      {/* ===== 顶部标题栏 ===== */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-sky-400" />
          <span className="text-[14px] font-medium text-white">执行监控与告警</span>
          <span className="text-[10px] font-mono text-gray-600">
            {runtimeAvailable ? "实时同步" : "本地模式"}
          </span>
        </div>
        <button
          onClick={() => setShowAlertConfig(true)}
          className="flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
        >
          <Bell size={11} className="text-amber-400" />
          <span className="text-[11px] font-mono text-amber-300">告警规则</span>
          {activeAlerts.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-mono">
              {activeAlerts.length}
            </span>
          )}
        </button>
      </div>

      {/* ===== 主动告警横幅 ===== */}
      <AnimatePresence>
        {activeAlerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-1.5 overflow-hidden"
          >
            {activeAlerts.slice(0, 4).map((alert) => {
              const sev =
                alert.severity === "critical"
                  ? { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400" }
                  : alert.severity === "warning"
                  ? { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400" }
                  : { bg: "bg-sky-500/10", border: "border-sky-500/30", text: "text-sky-400" };
              return (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${sev.bg} ${sev.border}`}
                >
                  <AlertTriangle size={12} className={`${sev.text} shrink-0`} />
                  <span className={`text-[11px] font-mono flex-1 ${sev.text}`}>
                    {alert.message}
                  </span>
                  <span className="text-[9px] font-mono text-gray-600 shrink-0">
                    {formatTime(alert.createdAt)}
                  </span>
                  <button
                    onClick={() => dismissAlert(alert.id)}
                    className="text-gray-600 hover:text-gray-400 shrink-0"
                  >
                    <X size={11} />
                  </button>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== KPI 卡片区（5 个） ===== */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <MiniKpi label="运行中" value={kpis.running} icon={Loader2} color="#60A5FA" delay={0} />
        <MiniKpi
          label="今日成功"
          value={kpis.todaySuccess}
          icon={CheckCircle2}
          color="#34D399"
          delay={0.05}
        />
        <MiniKpi
          label="今日失败"
          value={kpis.todayFailed}
          icon={XCircle}
          color="#F87171"
          delay={0.1}
        />
        <MiniKpi
          label="平均耗时"
          value={formatDuration(kpis.avgDuration)}
          icon={Clock}
          color="#FBBF24"
          delay={0.15}
        />
        <MiniKpi
          label="成功率"
          value={`${kpis.successRate.toFixed(1)}%`}
          icon={TrendingUp}
          color="#A78BFA"
          delay={0.2}
        />
      </div>

      {/* ===== 中部：趋势图 + 慢节点 Top5 ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* 执行趋势图 */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
          className="lg:col-span-2 rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingUp size={13} className="text-emerald-400" />
              <span className="text-[12px] font-medium text-gray-200">
                执行趋势（最近 14 天）
              </span>
            </div>
            <div className="flex items-center gap-3 text-[10px] font-mono">
              <span className="flex items-center gap-1 text-emerald-400">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                成功
              </span>
              <span className="flex items-center gap-1 text-red-400">
                <span className="w-2 h-2 rounded-full bg-red-400" />
                失败
              </span>
            </div>
          </div>
          {hasData ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradSuccess" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34D399" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#34D399" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradFailed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F87171" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#F87171" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis
                  dataKey="label"
                  tick={{ fill: "#6B7280", fontSize: 10, fontFamily: "monospace" }}
                  axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: "#6B7280", fontSize: 10, fontFamily: "monospace" }}
                  axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "rgba(15,23,42,0.95)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 8,
                    fontSize: 11,
                    fontFamily: "monospace",
                  }}
                  labelStyle={{ color: "#9CA3AF" }}
                />
                <Area
                  type="monotone"
                  dataKey="success"
                  stroke="#34D399"
                  strokeWidth={1.5}
                  fill="url(#gradSuccess)"
                  name="成功"
                />
                <Area
                  type="monotone"
                  dataKey="failed"
                  stroke="#F87171"
                  strokeWidth={1.5}
                  fill="url(#gradFailed)"
                  name="失败"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState text="暂无执行记录" />
          )}
        </motion.div>

        {/* 慢节点 Top5 */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3"
        >
          <div className="flex items-center gap-2 mb-2">
            <Snail size={13} className="text-orange-400" />
            <span className="text-[12px] font-medium text-gray-200">慢节点 Top5</span>
          </div>
          {metrics.slowestNodes.length > 0 ? (
            <div className="space-y-1.5">
              {metrics.slowestNodes.map((node, idx) => {
                const maxDur = metrics.slowestNodes[0].avgDuration || 1;
                const pct = Math.min(100, (node.avgDuration / maxDur) * 100);
                return (
                  <div key={node.nodeId} className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-gray-600 w-3">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[11px] font-mono text-gray-300 truncate">
                          {node.nodeName}
                        </span>
                        <span className="text-[10px] font-mono text-orange-400 ml-2 shrink-0">
                          {formatDuration(node.avgDuration)}
                        </span>
                      </div>
                      <div className="h-1 rounded-full bg-white/[0.05] overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.5, delay: 0.4 + idx * 0.05 }}
                          className="h-full rounded-full"
                          style={{ background: "linear-gradient(90deg, #FB923C, #F87171)" }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState text="暂无节点数据" />
          )}
        </motion.div>
      </div>

      {/* ===== 底部：执行记录表格（可展开节点详情） ===== */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.35 }}
        className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <ListTree size={13} className="text-sky-400" />
            <span className="text-[12px] font-medium text-gray-200">执行记录</span>
            <span className="text-[10px] font-mono text-gray-600">
              ({executions.length})
            </span>
          </div>
          <div className="text-[10px] font-mono text-gray-600">
            成功率 {metrics.successRate.toFixed(1)}% · 失败率 {metrics.failureRate.toFixed(1)}%
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-[12px] text-gray-500">
            <Loader2 size={14} className="animate-spin mr-2" />
            加载中...
          </div>
        ) : !hasData ? (
          <EmptyState text="暂无执行记录" sub="工作流执行后会自动记录到此处" />
        ) : (
          <div className="max-h-[420px] overflow-y-auto">
            {/* 表头 */}
            <div className="grid grid-cols-12 gap-2 px-3 py-1.5 text-[10px] font-mono text-gray-600 uppercase tracking-wider border-b border-white/[0.04] sticky top-0 bg-[#0a0e1a]/95 backdrop-blur z-10">
              <div className="col-span-1" />
              <div className="col-span-3">工作流</div>
              <div className="col-span-2">状态</div>
              <div className="col-span-2">开始时间</div>
              <div className="col-span-1">耗时</div>
              <div className="col-span-1">触发</div>
              <div className="col-span-2 text-right">操作</div>
            </div>
            {sortedExecutions.map((exec) => {
              const expanded = expandedId === exec.id;
              const trigCfg = getTriggerConfig(exec.triggeredBy);
              const hasFailedNode = exec.nodeExecutions.some(
                (n) => n.status === "failed" || n.status === "timeout"
              );
              return (
                <div key={exec.id} className="border-b border-white/[0.03]">
                  {/* 行 */}
                  <div
                    className="grid grid-cols-12 gap-2 px-3 py-2 items-center hover:bg-white/[0.02] transition-colors cursor-pointer"
                    onClick={() => setExpandedId(expanded ? null : exec.id)}
                  >
                    <div className="col-span-1 flex items-center">
                      {expanded ? (
                        <ChevronDown size={12} className="text-gray-500" />
                      ) : (
                        <ChevronRight size={12} className="text-gray-500" />
                      )}
                    </div>
                    <div className="col-span-3 text-[11px] font-mono text-gray-200 truncate">
                      {exec.workflowName}
                    </div>
                    <div className="col-span-2">
                      <StatusBadge status={exec.status} />
                    </div>
                    <div className="col-span-2 text-[10px] font-mono text-gray-500">
                      {formatDateTime(exec.startTime)}
                    </div>
                    <div className="col-span-1 text-[10px] font-mono text-gray-400">
                      {formatDuration(exec.duration)}
                    </div>
                    <div className="col-span-1">
                      <span
                        className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                        style={{ color: trigCfg.color, background: `${trigCfg.color}15` }}
                      >
                        {trigCfg.label}
                      </span>
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setLogExecution(exec);
                        }}
                        className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-sky-400"
                        title="查看日志"
                      >
                        <Eye size={11} />
                      </button>
                      {hasFailedNode && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const failed = exec.nodeExecutions.find(
                              (n) => n.status === "failed" || n.status === "timeout"
                            );
                            void retryExecution(exec.id, failed?.nodeId);
                          }}
                          className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-emerald-400"
                          title="重试失败节点"
                        >
                          <RefreshCw size={11} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* 节点级详情 */}
                  <AnimatePresence>
                    {expanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-3 pb-2 pt-1 bg-white/[0.01]">
                          {exec.error && (
                            <div className="mb-2 px-2 py-1.5 rounded border border-red-500/20 bg-red-500/5 text-[11px] font-mono text-red-400">
                              <AlertTriangle size={10} className="inline mr-1" />
                              {exec.error}
                            </div>
                          )}
                          {exec.nodeExecutions.length === 0 ? (
                            <div className="text-[11px] font-mono text-gray-600 py-2 text-center">
                              暂无节点执行记录
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {exec.nodeExecutions.map((node) => {
                                const nodeRetryKey = `${exec.id}_${node.nodeId}`;
                                const isFailed =
                                  node.status === "failed" || node.status === "timeout";
                                return (
                                  <div
                                    key={node.nodeId}
                                    className="rounded border border-white/[0.04] bg-white/[0.01] p-2"
                                  >
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <Cpu size={10} className="text-gray-500" />
                                      <span className="text-[11px] font-mono text-gray-200">
                                        {node.nodeName}
                                      </span>
                                      <span className="text-[9px] font-mono text-gray-600">
                                        [{node.nodeType}]
                                      </span>
                                      <StatusBadge status={node.status} />
                                      {node.retryCount > 0 && (
                                        <span className="text-[9px] font-mono text-amber-400">
                                          重试 {node.retryCount}
                                        </span>
                                      )}
                                      <span className="text-[9px] font-mono text-gray-500 ml-auto">
                                        {formatDuration(node.duration)}
                                      </span>
                                      {isFailed && (
                                        <button
                                          onClick={() =>
                                            void retryExecution(exec.id, node.nodeId)
                                          }
                                          disabled={retrying[nodeRetryKey]}
                                          className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50"
                                        >
                                          {retrying[nodeRetryKey] ? (
                                            <Loader2 size={9} className="animate-spin" />
                                          ) : (
                                            <RefreshCw size={9} />
                                          )}
                                          重试
                                        </button>
                                      )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-[10px] font-mono">
                                      {node.input && (
                                        <div>
                                          <div className="text-gray-600 mb-0.5">输入</div>
                                          <div className="text-gray-400 bg-black/20 rounded p-1 max-h-16 overflow-y-auto whitespace-pre-wrap break-all">
                                            {node.input}
                                          </div>
                                        </div>
                                      )}
                                      {node.output && (
                                        <div>
                                          <div className="text-gray-600 mb-0.5">输出</div>
                                          <div className="text-emerald-400/80 bg-black/20 rounded p-1 max-h-16 overflow-y-auto whitespace-pre-wrap break-all">
                                            {node.output}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    {node.error && (
                                      <div className="mt-1 text-[10px] font-mono text-red-400 bg-red-500/5 border border-red-500/20 rounded p-1">
                                        {node.error}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* ===== 告警规则配置弹窗 ===== */}
      <AnimatePresence>
        {showAlertConfig && (
          <AlertConfigModal
            rules={alertRules}
            onSave={handleSaveAlertRules}
            onClose={() => setShowAlertConfig(false)}
          />
        )}
      </AnimatePresence>

      {/* ===== 日志查看弹窗 ===== */}
      <AnimatePresence>
        {logExecution && (
          <LogViewerModal
            execution={logExecution}
            onClose={() => setLogExecution(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：告警规则配置弹窗
// ============================================
interface AlertConfigModalProps {
  rules: AlertRule[];
  onSave: (rules: AlertRule[]) => void;
  onClose: () => void;
}

function AlertConfigModal({ rules, onSave, onClose }: AlertConfigModalProps) {
  const [draft, setDraft] = useState<AlertRule[]>(rules);

  const updateRule = (id: string, patch: Partial<AlertRule>) => {
    setDraft((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  // 各告警类型的展示配置
  const ruleTypeConfig: Record<
    AlertRule["type"],
    { label: string; desc: string; thresholdLabel: string; icon: typeof Bell }
  > = {
    failure: {
      label: "失败告警",
      desc: "执行失败时触发告警，可配置自动重试",
      thresholdLabel: "",
      icon: XCircle,
    },
    timeout: {
      label: "超时告警",
      desc: "执行运行时长超过阈值时告警",
      thresholdLabel: "超时阈值(ms)",
      icon: Clock,
    },
    slow_node: {
      label: "慢节点告警",
      desc: "节点耗时超过阈值时告警",
      thresholdLabel: "慢节点阈值(ms)",
      icon: Snail,
    },
    high_error_rate: {
      label: "高错误率告警",
      desc: "近期错误率超过阈值时告警",
      thresholdLabel: "错误率阈值(%)",
      icon: TrendingDown,
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-xl border border-white/[0.08] bg-[#0a0e1a] backdrop-blur-xl p-4 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Settings size={14} className="text-amber-400" />
            <span className="text-[13px] font-medium text-white">告警规则配置</span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300">
            <X size={14} />
          </button>
        </div>

        <div className="space-y-2">
          {draft.map((rule) => {
            const cfg = ruleTypeConfig[rule.type];
            const Icon = cfg.icon;
            return (
              <div
                key={rule.id}
                className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Icon size={12} className="text-amber-400" />
                    <span className="text-[12px] font-medium text-gray-200">
                      {cfg.label}
                    </span>
                  </div>
                  {/* 启用开关 */}
                  <button
                    onClick={() => updateRule(rule.id, { enabled: !rule.enabled })}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      rule.enabled ? "bg-emerald-500/60" : "bg-white/[0.08]"
                    }`}
                  >
                    <motion.span
                      animate={{ x: rule.enabled ? 16 : 2 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-0.5 w-4 h-4 rounded-full bg-white"
                    />
                  </button>
                </div>
                <div className="text-[10px] font-mono text-gray-600 mb-2">
                  {cfg.desc}
                </div>

                {/* 阈值配置 */}
                {cfg.thresholdLabel && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-mono text-gray-500 w-24">
                      {cfg.thresholdLabel}
                    </span>
                    <input
                      type="number"
                      value={rule.threshold ?? 0}
                      onChange={(e) =>
                        updateRule(rule.id, { threshold: Number(e.target.value) })
                      }
                      className="flex-1 px-2 py-1 rounded bg-black/30 border border-white/[0.06] text-[11px] font-mono text-gray-200 focus:outline-none focus:border-amber-500/40"
                    />
                  </div>
                )}

                {/* 重试配置（仅失败告警） */}
                {rule.type === "failure" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-gray-500">最大重试</span>
                      <input
                        type="number"
                        min={0}
                        max={10}
                        value={rule.retryConfig?.maxRetries ?? 0}
                        onChange={(e) =>
                          updateRule(rule.id, {
                            retryConfig: {
                              maxRetries: Number(e.target.value),
                              interval: rule.retryConfig?.interval ?? 5,
                            },
                          })
                        }
                        className="w-16 px-2 py-1 rounded bg-black/30 border border-white/[0.06] text-[11px] font-mono text-gray-200 focus:outline-none focus:border-amber-500/40"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-gray-500">间隔(秒)</span>
                      <input
                        type="number"
                        min={1}
                        value={rule.retryConfig?.interval ?? 5}
                        onChange={(e) =>
                          updateRule(rule.id, {
                            retryConfig: {
                              maxRetries: rule.retryConfig?.maxRetries ?? 3,
                              interval: Number(e.target.value),
                            },
                          })
                        }
                        className="w-16 px-2 py-1 rounded bg-black/30 border border-white/[0.06] text-[11px] font-mono text-gray-200 focus:outline-none focus:border-amber-500/40"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-[11px] font-mono text-gray-400 border border-white/[0.06] hover:bg-white/[0.04]"
          >
            取消
          </button>
          <button
            onClick={() => onSave(draft)}
            className="px-3 py-1.5 rounded-md text-[11px] font-mono text-amber-300 border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20"
          >
            保存
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：执行日志查看弹窗（按时间线聚合节点级日志）
// ============================================
function LogViewerModal({
  execution,
  onClose,
}: {
  execution: WorkflowExecution;
  onClose: () => void;
}) {
  // 拼接完整执行日志（按时间线）
  const logLines = useMemo(() => {
    const lines: { time: string; level: "INFO" | "WARN" | "ERROR"; msg: string }[] = [];
    lines.push({
      time: execution.startTime,
      level: "INFO",
      msg: `工作流「${execution.workflowName}」开始执行（触发方式：${execution.triggeredBy}）`,
    });
    for (const node of execution.nodeExecutions) {
      lines.push({
        time: node.startTime,
        level: node.status === "failed" || node.status === "timeout" ? "ERROR" : "INFO",
        msg: `节点 [${node.nodeName}] 开始执行（类型：${node.nodeType}）`,
      });
      if (node.input) {
        lines.push({ time: node.startTime, level: "INFO", msg: `  输入：${node.input}` });
      }
      if (node.output) {
        lines.push({
          time: node.endTime || node.startTime,
          level: "INFO",
          msg: `  输出：${node.output}`,
        });
      }
      if (node.error) {
        lines.push({
          time: node.endTime || node.startTime,
          level: "ERROR",
          msg: `  错误：${node.error}`,
        });
      }
      if (node.retryCount > 0) {
        lines.push({
          time: node.endTime || node.startTime,
          level: "WARN",
          msg: `  节点已重试 ${node.retryCount} 次`,
        });
      }
      lines.push({
        time: node.endTime || node.startTime,
        level: node.status === "success" ? "INFO" : "WARN",
        msg: `节点 [${node.nodeName}] 结束，状态：${STATUS_CONFIG[node.status].label}，耗时：${formatDuration(node.duration)}`,
      });
    }
    if (execution.endTime) {
      lines.push({
        time: execution.endTime,
        level: execution.status === "success" ? "INFO" : "ERROR",
        msg: `工作流执行结束，状态：${STATUS_CONFIG[execution.status].label}，总耗时：${formatDuration(execution.duration)}`,
      });
    }
    if (execution.error) {
      lines.push({
        time: execution.endTime || execution.startTime,
        level: "ERROR",
        msg: `工作流错误：${execution.error}`,
      });
    }
    // 按时间排序
    return lines.sort(
      (a, b) => new Date(a.time || 0).getTime() - new Date(b.time || 0).getTime()
    );
  }, [execution]);

  const levelColor: Record<"INFO" | "WARN" | "ERROR", string> = {
    INFO: "text-sky-400",
    WARN: "text-amber-400",
    ERROR: "text-red-400",
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-xl border border-white/[0.08] bg-[#0a0e1a] backdrop-blur-xl flex flex-col max-h-[80vh]"
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 flex-wrap">
            <Eye size={14} className="text-sky-400" />
            <span className="text-[13px] font-medium text-white">执行日志</span>
            <span className="text-[10px] font-mono text-gray-600">
              {execution.workflowName} · {formatDateTime(execution.startTime)}
            </span>
            <StatusBadge status={execution.status} />
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300">
            <X size={14} />
          </button>
        </div>
        {/* 日志内容 */}
        <div className="flex-1 overflow-y-auto p-3 font-mono text-[11px] space-y-0.5">
          {logLines.map((line, idx) => (
            <div
              key={idx}
              className="flex gap-2 items-start hover:bg-white/[0.02] px-1 py-0.5 rounded"
            >
              <span className="text-gray-600 shrink-0">{formatTime(line.time)}</span>
              <span className={`shrink-0 w-12 ${levelColor[line.level]}`}>
                [{line.level}]
              </span>
              <span className="text-gray-300 break-all whitespace-pre-wrap">
                {line.msg}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
